/**
 * useOnline — navigator.onLine + event listener.
 *
 * Returns `true` when the browser reports a network connection,
 * `false` when offline.  Updates reactively on the `online` /
 * `offline` window events.
 *
 * SSR-safe: defaults to `true` when `navigator` is unavailable
 * (server-side render or non-browser environments).
 *
 * Spec: docs/CHAT-UI-IMPROVEMENT-DESIGN.md §3.1 + §3.6
 */
import { useState, useEffect } from 'react';

export function useOnline(): boolean {
  const getOnline = (): boolean => {
    if (typeof navigator === 'undefined') return true;
    return navigator.onLine;
  };

  const [online, setOnline] = useState<boolean>(getOnline);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);

    // Sync immediately in case state changed between render and mount.
    setOnline(navigator.onLine);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return online;
}
